import { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Upload,
  FileUp,
  FolderUp,
  Sparkles,
  FileText,
  Image,
  Database,
  Archive,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Trash2,
  Eye,
  Edit,
  Play,
  Pause,
  RotateCcw,
  Settings,
  Zap,
  Brain,
  Tag,
  MapPin,
  Calendar,
  User,
  Building2,
  Link2,
  ChevronRight
} from "lucide-react";

// Mock upload queue data
const uploadQueue = [
  {
    id: 1,
    filename: "coral_reef_survey_2024.csv",
    size: "45.2 MB",
    type: "dataset",
    status: "completed",
    progress: 100,
    aiExtracted: {
      title: "Great Barrier Reef Survey Data 2024",
      description: "Comprehensive survey data from 45 reef sites including coral coverage, species diversity, and water quality metrics.",
      keywords: ["coral reef", "biodiversity", "marine science", "Great Barrier Reef"],
      spatialCoverage: "Great Barrier Reef, Queensland, Australia",
      temporalCoverage: "2024-01-01 to 2024-06-30",
      creator: "Marine Research Institute",
      license: "CC-BY-4.0"
    }
  },
  {
    id: 2,
    filename: "genomics_batch_001.zip",
    size: "2.3 GB",
    type: "collection",
    status: "processing",
    progress: 67,
    aiExtracted: null
  },
  {
    id: 3,
    filename: "climate_model_output.nc",
    size: "890 MB",
    type: "dataset",
    status: "queued",
    progress: 0,
    aiExtracted: null
  },
  {
    id: 4,
    filename: "fieldwork_photos.tar",
    size: "5.6 GB",
    type: "collection",
    status: "failed",
    progress: 23,
    error: "Archive corrupted at byte 1234567890",
    aiExtracted: null
  },
  {
    id: 5,
    filename: "sensor_readings_jan2024.json",
    size: "128 MB",
    type: "dataset",
    status: "completed",
    progress: 100,
    aiExtracted: {
      title: "Environmental Sensor Network Readings - January 2024",
      description: "Real-time sensor data from 120 monitoring stations across NSW including temperature, humidity, and air quality measurements.",
      keywords: ["sensor data", "environmental monitoring", "air quality", "temperature"],
      spatialCoverage: "New South Wales, Australia",
      temporalCoverage: "2024-01-01 to 2024-01-31",
      creator: "Environmental Monitoring Agency",
      license: "CC-BY-NC-4.0"
    }
  }
];

const recentUploads = [
  { date: "2024-01-15", count: 12, size: "4.5 GB" },
  { date: "2024-01-14", count: 8, size: "2.1 GB" },
  { date: "2024-01-13", count: 23, size: "8.9 GB" },
  { date: "2024-01-12", count: 5, size: "1.2 GB" },
  { date: "2024-01-11", count: 17, size: "6.3 GB" }
];

const aiExtractionSettings = {
  autoExtractMetadata: true,
  autoDetectFormat: true,
  autoGenerateKeywords: true,
  autoSuggestLicense: true,
  autoLinkRelated: true,
  confidenceThreshold: 0.75
};

type FileUpload = {
  id: number;
  filename: string;
  size: string;
  type: string;
  status: string;
  progress: number;
  error?: string;
  aiExtracted: {
    title: string;
    description: string;
    keywords: string[];
    spatialCoverage: string;
    temporalCoverage: string;
    creator: string;
    license: string;
  } | null;
};

export default function ResearchUploadsPage() {
  const [activeTab, setActiveTab] = useState("upload");
  const [dragActive, setDragActive] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState<FileUpload | null>(null);
  const [showMetadataDialog, setShowMetadataDialog] = useState(false);
  const [settings, setSettings] = useState(aiExtractionSettings);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    // Handle file drop - in real implementation would process files
    console.log("Files dropped:", e.dataTransfer.files);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "processing":
        return <Clock className="h-5 w-5 text-blue-500 animate-pulse" />;
      case "queued":
        return <Clock className="h-5 w-5 text-gray-400" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case "dataset":
        return <Database className="h-5 w-5 text-blue-500" />;
      case "collection":
        return <Archive className="h-5 w-5 text-purple-500" />;
      case "image":
        return <Image className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const viewExtractedMetadata = (upload: FileUpload) => {
    setSelectedUpload(upload);
    setShowMetadataDialog(true);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Upload className="h-8 w-8 text-primary" />
            Research Uploads
          </h1>
          <p className="text-muted-foreground mt-1">
            Bulk upload with AI-powered metadata extraction
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="gap-2 py-1.5">
            <Sparkles className="h-4 w-4 text-yellow-500" />
            AI Extraction: Active
          </Badge>
          <Button variant="outline" size="icon">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                <FileUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">156</p>
                <p className="text-sm text-muted-foreground">Total Uploads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">142</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">8</p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">89%</p>
                <p className="text-sm text-muted-foreground">AI Accuracy</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload" className="gap-2">
            <FileUp className="h-4 w-4" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="queue" className="gap-2">
            <Clock className="h-4 w-4" />
            Queue
            <Badge variant="secondary" className="ml-1">5</Badge>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            AI Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload Zone */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Upload Files</CardTitle>
                <CardDescription>
                  Drag and drop files or folders, or click to browse
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                    dragActive
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary/50"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 rounded-full bg-primary/10">
                      <FolderUp className="h-12 w-12 text-primary" />
                    </div>
                    <div>
                      <p className="text-lg font-medium">
                        Drop files or folders here
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Supports CSV, JSON, XML, NetCDF, ZIP, TAR, and more
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button>
                        <FileUp className="h-4 w-4 mr-2" />
                        Browse Files
                      </Button>
                      <Button variant="outline">
                        <FolderUp className="h-4 w-4 mr-2" />
                        Browse Folder
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Upload Options */}
                <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    <span className="font-medium">AI Processing Options</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="auto-extract">Auto-extract metadata</Label>
                      <Switch id="auto-extract" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="auto-keywords">Generate keywords</Label>
                      <Switch id="auto-keywords" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="auto-link">Link related records</Label>
                      <Switch id="auto-link" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="auto-license">Suggest license</Label>
                      <Switch id="auto-license" defaultChecked />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Upload history</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentUploads.map((day, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{day.date}</p>
                      <p className="text-sm text-muted-foreground">
                        {day.count} files
                      </p>
                    </div>
                    <Badge variant="outline">{day.size}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="queue" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Upload Queue</CardTitle>
                  <CardDescription>
                    Monitor and manage file processing
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Play className="h-4 w-4 mr-2" />
                    Resume All
                  </Button>
                  <Button variant="outline" size="sm">
                    <Pause className="h-4 w-4 mr-2" />
                    Pause All
                  </Button>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Completed
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {uploadQueue.map((upload) => (
                  <div
                    key={upload.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    {getFileIcon(upload.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{upload.filename}</p>
                        <Badge variant="outline">{upload.size}</Badge>
                      </div>
                      {upload.status === "processing" && (
                        <Progress value={upload.progress} className="h-2 mt-2" />
                      )}
                      {upload.status === "failed" && upload.error && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {upload.error}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(upload.status)}
                      <Badge
                        variant={
                          upload.status === "completed"
                            ? "default"
                            : upload.status === "failed"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {upload.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {upload.aiExtracted && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewExtractedMetadata(upload)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {upload.status === "failed" && (
                        <Button variant="ghost" size="icon">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-500" />
                  AI Extraction Settings
                </CardTitle>
                <CardDescription>
                  Configure automatic metadata extraction behavior
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-extract Metadata</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically extract title, description, and other metadata
                      </p>
                    </div>
                    <Switch
                      checked={settings.autoExtractMetadata}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, autoExtractMetadata: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-detect Format</Label>
                      <p className="text-sm text-muted-foreground">
                        Identify file format and schema automatically
                      </p>
                    </div>
                    <Switch
                      checked={settings.autoDetectFormat}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, autoDetectFormat: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Generate Keywords</Label>
                      <p className="text-sm text-muted-foreground">
                        AI-powered keyword and tag suggestions
                      </p>
                    </div>
                    <Switch
                      checked={settings.autoGenerateKeywords}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, autoGenerateKeywords: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Suggest License</Label>
                      <p className="text-sm text-muted-foreground">
                        Recommend appropriate data license
                      </p>
                    </div>
                    <Switch
                      checked={settings.autoSuggestLicense}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, autoSuggestLicense: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Link Related Records</Label>
                      <p className="text-sm text-muted-foreground">
                        Find and suggest related datasets and publications
                      </p>
                    </div>
                    <Switch
                      checked={settings.autoLinkRelated}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, autoLinkRelated: checked })
                      }
                    />
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Label>Confidence Threshold</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Minimum confidence level for AI suggestions
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0.5"
                      max="0.95"
                      step="0.05"
                      value={settings.confidenceThreshold}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          confidenceThreshold: parseFloat(e.target.value)
                        })
                      }
                      className="flex-1"
                    />
                    <Badge variant="outline">
                      {(settings.confidenceThreshold * 100).toFixed(0)}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  Extraction Capabilities
                </CardTitle>
                <CardDescription>
                  What the AI can automatically detect and extract
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Tag className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Keywords & Tags</p>
                      <p className="text-sm text-muted-foreground">
                        Subject terms, ANZSRC codes, FOR codes
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <MapPin className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">Spatial Coverage</p>
                      <p className="text-sm text-muted-foreground">
                        Coordinates, regions, place names
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="font-medium">Temporal Coverage</p>
                      <p className="text-sm text-muted-foreground">
                        Date ranges, time periods, frequency
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <User className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium">Contributors</p>
                      <p className="text-sm text-muted-foreground">
                        Authors, ORCIDs, affiliations
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Building2 className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="font-medium">Institutions</p>
                      <p className="text-sm text-muted-foreground">
                        Organizations, ROR IDs, funders
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Link2 className="h-5 w-5 text-cyan-500" />
                    <div>
                      <p className="font-medium">Related Works</p>
                      <p className="text-sm text-muted-foreground">
                        Publications, datasets, DOIs
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Metadata Preview Dialog */}
      <Dialog open={showMetadataDialog} onOpenChange={setShowMetadataDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              AI Extracted Metadata
            </DialogTitle>
            <DialogDescription>
              Review and edit the automatically extracted metadata
            </DialogDescription>
          </DialogHeader>
          {selectedUpload?.aiExtracted && (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input defaultValue={selectedUpload.aiExtracted.title} className="mt-1" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  defaultValue={selectedUpload.aiExtracted.description}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Keywords</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedUpload.aiExtracted.keywords.map((keyword, index) => (
                    <Badge key={index} variant="secondary" className="gap-1">
                      {keyword}
                      <XCircle className="h-3 w-3 cursor-pointer hover:text-red-500" />
                    </Badge>
                  ))}
                  <Badge variant="outline" className="cursor-pointer">
                    + Add
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Spatial Coverage</Label>
                  <Input
                    defaultValue={selectedUpload.aiExtracted.spatialCoverage}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Temporal Coverage</Label>
                  <Input
                    defaultValue={selectedUpload.aiExtracted.temporalCoverage}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Creator</Label>
                  <Input defaultValue={selectedUpload.aiExtracted.creator} className="mt-1" />
                </div>
                <div>
                  <Label>License</Label>
                  <Select defaultValue={selectedUpload.aiExtracted.license}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC-BY-4.0">CC-BY-4.0</SelectItem>
                      <SelectItem value="CC-BY-NC-4.0">CC-BY-NC-4.0</SelectItem>
                      <SelectItem value="CC-BY-SA-4.0">CC-BY-SA-4.0</SelectItem>
                      <SelectItem value="CC0">CC0 (Public Domain)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetadataDialog(false)}>
              Cancel
            </Button>
            <Button>
              <Edit className="h-4 w-4 mr-2" />
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
